import React, { useState, useRef, Fragment } from "react"
import PropTypes from "prop-types"
import { connect, useSelector } from "react-redux"
import { Toast, Image } from "react-bootstrap"
import { useFormik } from "formik"
import { string, object } from "yup"
import { useTranslation } from "react-i18next"
import {
  HiEyeOff,
  HiEye,
  HiTrash,
  HiOutlinePhotograph,
  HiLocationMarker,
  HiChevronRight,
  HiPhone,
  HiChatAlt2
} from "react-icons/hi"
import { Switch } from "@headlessui/react"
import { updateDestinationWithFile } from "../../../actions/destinations"
import { capitalize, twClassNames } from "../../../helpers"
import DragZone from "../../../components/dragZone"
import Tooltip from "../../../components/tooltip"
import Emulator from "../../../components/emulator"
import defaultBackground from "../../../assets/city_background.jpg"
import { getLocaleValue } from "../../../helpers/getLocaleValue"
import { MAX_IMAGE_FILE_SIZE } from "../../../constants/fileHandling"
import { isLevi } from "../../../helpers/isLevi"

const validationSchema = t => {
  return object().shape(
    {
      nameEn: string()
        .when(["nameFi"], {
          is: nameFi => !nameFi,
          then: () => string().required(t("common.validation.eitherRequired"))
        })
        .min(3, t("common.validation.invalidMinLength"))
        .max(120, t("common.validation.invalidMaxLength")),
      nameFi: string()
        .when(["nameEn"], {
          is: nameEn => !nameEn,
          then: () => string().required(t("common.validation.eitherRequired"))
        })
        .min(3, t("common.validation.invalidMinLength"))
        .max(120, t("common.validation.invalidMaxLength")),
      streetName: string()
        .min(3, t("common.validation.invalidMinLength"))
        .max(120, t("common.validation.invalidMaxLength"))
        .required(t("common.validation.required")),
      city: string()
        .min(3, t("common.validation.invalidMinLength"))
        .max(30, t("common.validation.invalidMaxLength"))
        .required(t("common.validation.required")),
      country: string()
        .min(3, t("common.validation.invalidMinLength"))
        .max(30, t("common.validation.invalidMaxLength")),
      phone: string()
        .min(6, t("common.validation.invalidMinLength"))
        .max(30, t("common.validation.invalidMaxLength"))
        .matches(/^\+(?:[0-9]?){6,14}[0-9]$/, t("common.validation.invalidPhone")),
      descriptionEn: string().when(["descriptionFi"], {
        is: descriptionFi => !descriptionFi,
        then: () => string().required(t("common.validation.eitherRequired"))
      }),
      descriptionFi: string().when(["descriptionEn"], {
        is: descriptionEn => !descriptionEn,
        then: () => string().required(t("common.validation.eitherRequired"))
      }),
      pincode: string()
        .matches(/^\d+$/, t("common.validation.digitsOnly"))
        .min(4, t("common.validation.invalidMinLength"))
        .max(4, t("common.validation.invalidMaxLength")),
      accessCode: string()
        .min(5, t("common.validation.invalidMinLength"))
        .max(8, t("common.validation.invalidMaxLength"))
        .required(t("common.validation.required"))
        .matches(/^[A-Z0-9]+$/, t("common.validation.onlyUppercaseLettersAndNumbers"))
    },
    [
      ["nameFi", "nameEn"],
      ["descriptionFi", "descriptionEn"]
    ]
  )
}

const Preview = ({ data, t }) => {
  const [lng, setPreviewLanguage] = useState("fi")

  const image = data.image
    ? typeof data.image === "string"
      ? data.image
      : URL.createObjectURL(data.image)
    : defaultBackground

  return (
    <Emulator lng={lng} setPreviewLanguage={setPreviewLanguage}>
      <div className="tw-h-full tw-w-full tw-rounded-[25px] tw-bg-light">
        {/* Image */}
        <div className="tw-relative tw-h-[247px] tw-w-full tw-bg-dark-25 tw-rounded-t-[25px]">
          <img alt="" src={image} className="tw-h-full tw-w-full tw-object-cover tw-rounded-t-[25px]" />
        </div>
        {/* Text */}
        <div className="tw-absolute tw-top-48 tw-px-[15px]">
          <div className="tw-bg-white tw-border tw-rounded-2xl tw-p-2.5 tw-w-[260px]">
            <div className="tw-text-xs tw-text-dark tw-mb-2">
              <HiLocationMarker className="tw-text-sm tw-mr-2" />
              {data.streetName}, {data?.postal_code} {capitalize(data?.city)}
            </div>
            <div className="tw-text-xs tw-mb-2">
              {getLocaleValue(data, "description", lng) && (
                <p className="tw-text-xs tw-text-dark-75 tw-line-clamp-6">{getLocaleValue(data, "description", lng)}</p>
              )}
            </div>
            <div className="tw-flex tw-space-x-2">
              {data.displayCall && (
                <button
                  type="button"
                  className="tw-w-1/2 tw-btn tw-h-[32px] tw-min-w-[20px] tw-btn-primary tw-flex tw-items-center
                    tw-justify-start">
                  <HiPhone className="tw-text-sm" />
                  <span className="tw-text-sm">{t("settings.emulator.call", { lng })}</span>
                </button>
              )}
              {data.displayMessage && (
                <button
                  type="button"
                  className="tw-w-1/2 tw-btn tw-h-[32px] tw-min-w-[20px] tw-btn-primary tw-flex tw-items-center
                    tw-justify-start">
                  <HiChatAlt2 className="tw-text-sm" />
                  <span className="tw-text-sm">{t("settings.emulator.message", { lng })}</span>
                </button>
              )}
            </div>
          </div>
          {["1", "2", "3", "4", "5"].map(item => (
            <div
              key={item}
              className="tw-flex tw-w-full tw-h-9 tw-mt-2.5 tw-px-4 tw-bg-white tw-border tw-rounded-2xl tw-shadow
                tw-items-center tw-justify-between">
              <span className="tw-text-sm">{t("settings.emulator.guide", { value: item, lng })}</span>
              <HiChevronRight className="tw-text-sm" />
            </div>
          ))}
        </div>
      </div>
    </Emulator>
  )
}

const Form = ({ formik, isLoading, t }) => {
  const imageRef = useRef()
  const [showPincode, setShowPincode] = useState(false)
  const teams = useSelector(state => state.team.teams)
  const userRole = teams?.find(team => team.id === formik.values?.ownerTeamId)?.userRole

  const onReset = () => {
    formik.handleReset()
    imageRef.current.value = null //Fix: reset image file selector
  }

  const isValidCLass = field => formik.touched[field] && formik.errors[field] && "tw-is-invalid"

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <div className="tw-w-full md:tw-pr-6 max-md:tw-pb-6">
        <fieldset className="tw-grid tw-grid-cols md:tw-grid-cols-6 tw-gap-x-2 tw-gap-y-3 md:tw-gap-y-4">
          {/* Active */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="isPublic" className="tw-form-label">
              {t("settings.form.isPublic")}
            </label>
            <div className="tw-flex tw-space-x-2">
              <Switch
                disabled={userRole === "levi_maintainer" ?? false}
                id="isPublic"
                name="isPublic"
                className="tw-items-center"
                checked={formik.values.isPublic}
                onChange={value => formik.setFieldValue("isPublic", value)}
                as={Fragment}>
                {({ checked }) => (
                  <span className="tw-flex">
                    <button
                      disabled={userRole === "levi_maintainer" ?? false}
                      className={twClassNames(
                        checked ? "tw-bg-primary" : "tw-bg-dark-25",
                        "tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-items-center tw-rounded-full",
                        `disabled:tw-opacity-25 focus-within:tw-outline-none focus-within:tw-ring-2
                        focus-within:tw-ring-primary-50`
                      )}>
                      <span
                        className={twClassNames(
                          checked ? "tw-translate-x-6" : "tw-translate-x-1",
                          "tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-transition"
                        )}
                      />
                    </button>
                    <span className="tw-ml-3 tw-text-sm">
                      {checked ? t("settings.form.active") : t("settings.form.unactive")}
                    </span>
                  </span>
                )}
              </Switch>
            </div>
          </div>
          {/* Access code */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="accessCode" className="tw-form-label">
              {t("settings.form.accessCode")}
              <Tooltip message="settings.tooltips.accessCode" />
            </label>
            <input
              disabled={!isLevi(formik.values.ownerTeamId) || userRole === "levi_maintainer"}
              type="text"
              id="accessCode"
              name="accessCode"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.accessCode}
              className={twClassNames(isValidCLass("accessCode"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.accessCode}</div>
          </div>
          {/* Pincode */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="pincode" className="tw-form-label">
              {t("settings.form.pincode")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
              <Tooltip message="settings.tooltips.pincode" />
            </label>
            <span className="tw-relative">
              <input
                disabled={userRole === "levi_maintainer" ?? false}
                type={showPincode ? "text" : "password"}
                id="pincode"
                name="pincode"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.pincode}
                maxLength={4}
                className={twClassNames(isValidCLass("pincode"), "tw-form-control !tw-pr-9")}
                spellCheck="false"
              />
              <button
                type="button"
                onClick={() => setShowPincode(!showPincode)}
                className={twClassNames("tw-absolute tw-right-2.5 tw-top-0 tw-my-1.5 tw-bg-white")}>
                {showPincode ? <HiEyeOff /> : <HiEye />}
              </button>
              <div className="tw-invalid-tooltip">{formik.errors.pincode}</div>
            </span>
          </div>
          {/* Basic informations */}
          <div className="tw-relative tw-col-span-6 tw-border-b tw-mt-4">
            <h5>{t("settings.form.subtitle.name")}</h5>
            <p className="tw-text-sm tw-text-dark-75">{t("settings.form.subtitle.info")}</p>
          </div>
          {/* Name (fi) */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="nameFi" className="tw-form-label">
              {t("settings.form.nameFi")}
            </label>
            <input
              type="text"
              id="nameFi"
              name="nameFi"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nameFi}
              className={twClassNames(isValidCLass("nameFi"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.nameFi}</div>
          </div>
          {/* Name (eng) */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="nameEn" className="tw-form-label">
              {t("settings.form.nameEn")}
            </label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nameEn}
              className={twClassNames(isValidCLass("nameEn"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.nameEn}</div>
          </div>
          {/* Street Name */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="streetName" className="tw-form-label">
              {t("settings.form.streetName")}
            </label>
            <input
              type="text"
              id="streetName"
              name="streetName"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.streetName}
              className={twClassNames(isValidCLass("streetName"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.streetName}</div>
          </div>
          {/* City */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="city" className="tw-form-label">
              {t("settings.form.city")}
            </label>
            <input
              type="text"
              id="city"
              name="city"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={capitalize(formik.values.city)}
              className={twClassNames(isValidCLass("city"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.city}</div>
          </div>
          {/* Phone */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="phone" className="tw-form-label">
              {t("settings.form.phone")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
              <Tooltip message="settings.tooltips.phone" />
            </label>
            <input
              disabled={userRole === "levi_maintainer" ?? false}
              type="phone"
              id="phone"
              name="phone"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phone}
              className={twClassNames(isValidCLass("phone"), "tw-form-control")}
            />
            <div className="tw-invalid-tooltip">{formik.errors.phone}</div>
          </div>
          {/* Description FI */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="descriptionFi" className="tw-form-label">
              {t("settings.form.descriptionFi")}
              <Tooltip message="settings.tooltips.descriptionFi" />
            </label>
            <textarea
              disabled={userRole === "levi_maintainer" ?? false}
              type="text"
              id="descriptionFi"
              name="descriptionFi"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.descriptionFi}
              className={twClassNames(isValidCLass("descriptionFi"), "tw-form-control tw-min-h-[150px]")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.descriptionFi}</div>
          </div>
          {/* Description EN */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="descriptionEn" className="tw-form-label">
              {t("settings.form.descriptionEn")}
            </label>
            <textarea
              disabled={userRole === "levi_maintainer" ?? false}
              type="text"
              id="descriptionEn"
              name="descriptionEn"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.descriptionEn}
              className={twClassNames(isValidCLass("descriptionEn"), "tw-form-control tw-min-h-[150px]")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.descriptionEn}</div>
          </div>
          {/*  Call button */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3 lg:tw-col-span-2">
            <label htmlFor="displayCall" className="tw-form-label">
              {t("settings.form.displayCall")}
            </label>
            <div className="tw-flex tw-space-x-2">
              <Switch
                disabled={userRole === "levi_maintainer" ?? false}
                id="displayCall"
                name="displayCall"
                className="tw-items-center disabled:tw-opacity-25"
                checked={formik.values.displayCall}
                onChange={value => formik.setFieldValue("displayCall", value)}
                as={Fragment}>
                {({ checked }) => (
                  <button
                    className={twClassNames(
                      checked ? "tw-bg-primary" : "tw-bg-dark-25",
                      "tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-items-center tw-rounded-full",
                      "focus-within:tw-outline-none focus-within:tw-ring-2 focus-within:tw-ring-primary-50"
                    )}>
                    <span
                      className={twClassNames(
                        checked ? "tw-translate-x-6" : "tw-translate-x-1",
                        "tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-transition"
                      )}
                    />
                  </button>
                )}
              </Switch>
            </div>
          </div>
          {/* Message button */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3 lg:tw-col-span-2">
            <label htmlFor="displayMessage" className="tw-form-label">
              {t("settings.form.displayMessage")}
            </label>
            <div className="tw-flex tw-space-x-2">
              <Switch
                disabled={userRole === "levi_maintainer" ?? false}
                id="displayMessage"
                name="displayMessage"
                className="tw-items-center disabled:tw-opacity-25"
                checked={formik.values.displayMessage}
                onChange={value => formik.setFieldValue("displayMessage", value)}
                as={Fragment}>
                {({ checked }) => (
                  <button
                    className={twClassNames(
                      checked ? "tw-bg-primary" : "tw-bg-dark-25",
                      "tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-items-center tw-rounded-full",
                      "focus-within:tw-outline-none focus-within:tw-ring-2 focus-within:tw-ring-primary-50"
                    )}>
                    <span
                      className={twClassNames(
                        checked ? "tw-translate-x-6" : "tw-translate-x-1",
                        "tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-transition"
                      )}
                    />
                  </button>
                )}
              </Switch>
            </div>
          </div>
          {/* Image */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="image" className="tw-form-label">
              {t("settings.form.image")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
            </label>
            {formik.values.image ? (
              <div className="tw-relative tw-w-fit">
                <Image
                  thumbnail
                  onClick={() => imageRef.current.click()}
                  className={twClassNames("background-thumbnail", userRole === "levi_maintainer" && "tw-opacity-50")}
                  src={
                    typeof formik.values.image === "string"
                      ? formik.values.image
                      : URL.createObjectURL(formik.values.image)
                  }
                />
                <button
                  disabled={userRole === "levi_maintainer" ?? false}
                  type="button"
                  onClick={() => formik.setFieldValue("image", null)}
                  className="tw-absolute tw-bottom-0 tw-right-4 tw-bg-red-400 tw-rounded-full disabled:tw-opacity-50">
                  <HiTrash className="tw-text-white tw-h-8 tw-w-8 tw-p-2" />
                </button>
              </div>
            ) : (
              <DragZone
                onDrop={data => formik.setFieldValue("image", data)}
                onClick={() => imageRef.current.click()}
                className="tw-flex tw-justify-center tw-items-center tw-rounded-md tw-px-6 tw-py-4 tw-border-2
                  tw-border-dashed tw-cursor-pointer">
                <div className="tw-space-y-1 tw-text-center">
                  <HiOutlinePhotograph className="tw-mx-auto tw-h-12 tw-w-12 tw-text-dark-25 [&>*]:tw-stroke-1" />
                  <div className="tw-flex tw-text-sm tw-text-dark-75">
                    <label
                      htmlFor="image"
                      className="tw-relative tw-rounded-md tw-bg-white tw-font-medium tw-text-primary">
                      <span>{t("common.components.dragZone.upload")}</span>
                    </label>
                    <p className="tw-pl-1">{t("common.components.dragZone.dragAndDrop")}</p>
                  </div>
                  <p className="tw-text-xs tw-text-dark-50">{t("common.components.dragZone.type")}</p>
                </div>
              </DragZone>
            )}
            <input
              type="file"
              name="image"
              accept="image/*"
              className="tw-hidden tw-sr-only"
              ref={imageRef}
              onChange={event => formik.setFieldValue("image", event.currentTarget.files[0])}
            />
            <div className="tw-invalid-tooltip">{formik.errors.image}</div>
          </div>
        </fieldset>
      </div>
      {formik.dirty && (
        <div className="tw-fixed-footer tw-has-sidebar tw-bg-white tw-shadow-2xl tw-border-t tw-z-10 tw-py-4">
          <div className="tw-px-6 tw-flex tw-w-full tw-items-center tw-justify-between">
            <span>{t("common.unsavedChanges.content")}</span>
            <div className="tw-flex tw-flex-row tw-space-x-4">
              <button
                type="reset"
                disabled={isLoading}
                onClick={onReset}
                className="tw-btn tw-btn-outline tw-min-w-[180px]">
                {t("common.unsavedChanges.button.cancel")}
              </button>
              <button type="submit" disabled={isLoading} className="tw-btn tw-btn-primary tw-min-w-[180px]">
                {t("common.unsavedChanges.button.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  )
}

const SettingsContainer = props => {
  const { t } = useTranslation()
  const [toast, setToastFunc] = useState({ display: false, type: null, content: null })

  const setToast = (display, type, message, values) => {
    setToastFunc({ display, type, content: { message, values } })
  }

  const onSave = async ({ image, ...values }, actions) => {
    let data = {
      isPublic: values.isPublic,
      pincode: values.pincode,
      accessCode: values.accessCode,
      name: values.nameEn || values.nameFi,
      nameEn: values.nameEn,
      nameFi: values.nameFi,
      description: values.descriptionEn || values.descriptionFi,
      descriptionEn: values.descriptionEn,
      descriptionFi: values.descriptionFi,
      city: values.city.toLowerCase(),
      streetName: values.streetName,
      phone: values.phone,
      displayCall: values.displayCall,
      displayMessage: values.displayMessage
    }

    // TODO: Split image input to separate API request to simplify backend
    if (image?.name) {
      if (image?.size / (1024 * 1024) > MAX_IMAGE_FILE_SIZE) {
        setToast(true, "error", "image.maxSizeExceeded", { max_size: MAX_IMAGE_FILE_SIZE })
        return
      }
      data.image = image
    } else if (image === null) {
      data.deleteImage = true
    }

    props
      .updateDestinationWithFile(props.selectedDestinationId, data)
      .then(response => {
        actions.setSubmitting(false)
        actions.resetForm({ values: response.payload })
        setToast(true, "success", "save.success")
      })
      .catch(() => setToast(true, "error", "save.failed"))
  }

  const formik = useFormik({
    initialValues: {
      isPublic: props.destination?.isPublic ?? false,
      pincode: props.destination?.pincode || "",
      accessCode: props.destination?.accessCode || "",
      nameEn: props.destination?.nameEn || "",
      nameFi: props.destination?.nameFi || "",
      streetName: props.destination?.streetName || "",
      country: props.destination?.country || "",
      city: props.destination?.city || "",
      phone: props.destination?.phone || "",
      descriptionEn: props.destination?.descriptionEn || "",
      descriptionFi: props.destination?.descriptionFi || "",
      image: props.destination?.image || null,
      displayCall: props.destination?.displayCall ?? false,
      displayMessage: props.destination?.displayMessage ?? false,
      ownerTeamId: props.destination?.ownerTeamId
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validationSchema: validationSchema(t),
    onSubmit: (values, actions) => onSave(values, actions)
  })

  return (
    <>
      {toast && toast.display && (
        <Toast
          className={"fixed-toast " + toast.type}
          onClose={() => setToast(false)}
          show={toast.display}
          delay={5000}
          autohide>
          <Toast.Header>
            <strong className="mr-auto">{t("common.notifications.type." + toast.type)}</strong>
          </Toast.Header>
          <Toast.Body>{t("settings.notifications." + toast.content.message, toast.content.values)}</Toast.Body>
        </Toast>
      )}
      <h1 className="tw-font-display tw-font-semibold tw-text-4xl tw-mb-6">{t("settings.title")}</h1>
      <div className={twClassNames(formik.dirty ? "tw-mb-20" : "tw-mb-12", "tw-w-full")}>
        <div className="tw-mb-6 tw-shadow tw-bg-white tw-rounded">
          <div className="tw-flex tw-p-6 max-md:tw-flex-col">
            {/* Left side */}
            <div className="tw-w-full md:tw-w-1/2">
              <Form formik={formik} isLoading={props.isLoading} t={t} />
            </div>
            {/* Right side */}
            <div className="tw-w-full md:tw-w-1/2 tw-min-w-[360px] max-xs:tw-hidden">
              <Preview data={formik.values} t={t} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

SettingsContainer.propTypes = {
  updateDestinationWithFile: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
}

SettingsContainer.defaultProps = {
  isLoading: false
}

const mapStateToProps = state => ({
  isLoading: state.destinations.isLoading,
  selectedDestinationId: state.destinations.selectedDestinationId,
  destination: state.destinations.destinations.find(x => x.id === state.destinations.selectedDestinationId)
})

const mapDispatchToProps = dispatch => ({
  updateDestinationWithFile: (id, values) => dispatch(updateDestinationWithFile(id, values))
})

export default connect(mapStateToProps, mapDispatchToProps)(SettingsContainer)
